import type { ReactNode } from 'react';

export type StatCardIconTone = 'neutral' | 'warn' | 'danger' | 'success' | 'info';

export interface StatCardProps {
  readonly label: ReactNode;
  readonly value: ReactNode;
  readonly meta?: ReactNode;
  readonly trend?: 'up' | 'down' | 'flat';
  /** Optional 28×28 rounded square icon, top-right corner per mockup. */
  readonly icon?: ReactNode;
  readonly iconTone?: StatCardIconTone;
  readonly testId?: string;
  /** When provided, the card becomes an interactive button (click + keyboard). */
  readonly onClick?: () => void;
  /** Highlights the card with a brand ring (e.g. the active filter). */
  readonly active?: boolean;
}

const ICON_BG: Record<StatCardIconTone, string> = {
  neutral: 'var(--bg-sunken)',
  warn: 'var(--warn-50)',
  danger: 'var(--danger-50)',
  success: 'var(--success-50)',
  // Token (styles.css): brand-50 in light, a dark tint in dark theme — the raw
  // brand-50 rendered as a bright #eff5ff square on dark cards.
  info: 'var(--stat-icon-info-bg)',
};

const ICON_FG: Record<StatCardIconTone, string> = {
  neutral: 'var(--text-secondary)',
  warn: 'var(--warn-700)',
  danger: 'var(--danger-700)',
  success: 'var(--success-700)',
  info: 'var(--stat-icon-info-fg)',
};

export function StatCard({
  label,
  value,
  meta,
  trend,
  icon,
  iconTone = 'neutral',
  testId,
  onClick,
  active = false,
}: StatCardProps): JSX.Element {
  const interactive = typeof onClick === 'function';
  return (
    <div
      className="stat"
      data-testid={testId}
      data-active={active ? 'true' : undefined}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      aria-pressed={interactive ? active : undefined}
      onClick={onClick}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick?.();
              }
            }
          : undefined
      }
      style={
        interactive
          ? { cursor: 'pointer', boxShadow: active ? '0 0 0 2px var(--brand-500)' : undefined }
          : undefined
      }
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 8,
        }}
      >
        <div className="stat-label">{label}</div>
        {icon ? (
          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: 7,
              background: ICON_BG[iconTone],
              color: ICON_FG[iconTone],
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
            aria-hidden="true"
          >
            {icon}
          </div>
        ) : null}
      </div>
      <div className="stat-value t-num">{value}</div>
      {meta ? (
        <div
          className={['stat-meta', trend === 'up' ? 'up' : trend === 'down' ? 'down' : '']
            .filter(Boolean)
            .join(' ')}
        >
          {meta}
        </div>
      ) : null}
    </div>
  );
}
